import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for PWA "Add to Home Screen" functionality
 * 
 * Handles:
 * - Capturing the beforeinstallprompt event
 * - Detecting standalone mode (already installed)
 * - Session-based dismissal tracking
 * - iOS detection for fallback instructions
 */
export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  // Check if app is already installed (standalone mode)
  const checkIfInstalled = useCallback(() => {
    // Check display-mode: standalone
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    // iOS Safari standalone check
    const isIOSStandalone = window.navigator.standalone === true;
    // Android TWA check
    const referrer = document.referrer.includes('android-app://');
    
    return isStandalone || isIOSStandalone || referrer;
  }, []);

  // Check if user is on iOS
  const checkIfIOS = useCallback(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent) && !window.MSStream;
  }, []);

  // Check if already dismissed (permanently via localStorage)
  const checkIfDismissed = useCallback(() => {
    return localStorage.getItem('pwa-install-dismissed') === 'true';
  }, []);

  useEffect(() => {
    // Initial checks
    setIsInstalled(checkIfInstalled());
    setIsIOS(checkIfIOS());
    setIsDismissed(checkIfDismissed());

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      // Clear localStorage on install
      localStorage.removeItem('pwa-install-dismissed');
    };

    // Listen for display mode changes (e.g., user installs via browser UI)
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (e) => {
      if (e.matches) {
        setIsInstalled(true);
        setIsInstallable(false);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    mediaQuery.addEventListener('change', handleDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      mediaQuery.removeEventListener('change', handleDisplayModeChange);
    };
  }, [checkIfInstalled, checkIfIOS, checkIfDismissed]);

  // Trigger the install prompt
  const installApp = useCallback(async () => {
    if (!deferredPrompt) {
      console.warn('No deferred prompt available');
      return { success: false, outcome: 'no-prompt' };
    }

    setIsInstalling(true);

    try {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for user response
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstallable(false);
        setDeferredPrompt(null);
      }
      
      setIsInstalling(false);
      return { success: outcome === 'accepted', outcome };
    } catch (error) {
      console.error('Install prompt error:', error);
      setIsInstalling(false);
      return { success: false, outcome: 'error', error };
    }
  }, [deferredPrompt]);

  // Dismiss the install prompt permanently
  const dismissPrompt = useCallback(() => {
    localStorage.setItem('pwa-install-dismissed', 'true');
    setIsDismissed(true);
  }, []);

  // Reset dismissal (useful for testing or settings page)
  const resetDismissal = useCallback(() => {
    localStorage.removeItem('pwa-install-dismissed');
    setIsDismissed(false);
  }, []);

  // Should we show the install UI?
  const shouldShowInstallUI = 
    !isInstalled && 
    !isDismissed && 
    (isInstallable || isIOS);

  // Expose testing utilities on window for console debugging
  useEffect(() => {
    window.pwaTest = {
      reset: () => {
        localStorage.removeItem('pwa-install-dismissed');
        setIsDismissed(false);
        console.log('✅ PWA prompt reset. Refresh the page to see the prompt.');
      },
      dismiss: () => {
        localStorage.setItem('pwa-install-dismissed', 'true');
        setIsDismissed(true);
        console.log('✅ PWA prompt dismissed.');
      },
      status: () => {
        console.log({
          isInstalled,
          isInstallable,
          isIOS,
          isDismissed,
          shouldShowInstallUI,
          localStorage: localStorage.getItem('pwa-install-dismissed'),
        });
      },
      forceShow: () => {
        localStorage.removeItem('pwa-install-dismissed');
        setIsDismissed(false);
        setIsInstalled(false);
        console.log('✅ Forcing prompt to show. If on desktop, it may still need beforeinstallprompt event.');
      },
      help: () => {
        console.log(`
🔧 PWA Install Prompt Testing Commands:
────────────────────────────────────────
pwaTest.reset()     - Reset dismissal state (refresh to see prompt)
pwaTest.dismiss()   - Dismiss the prompt
pwaTest.status()    - Show current state
pwaTest.forceShow() - Force prompt to show
pwaTest.help()      - Show this help
        `);
      }
    };
    console.log('💡 PWA testing available: type pwaTest.help() for commands');
    
    return () => {
      delete window.pwaTest;
    };
  }, [isInstalled, isInstallable, isIOS, isDismissed, shouldShowInstallUI]);

  return {
    // State
    isInstallable,      // beforeinstallprompt fired
    isInstalled,        // Already running as PWA
    isIOS,              // User is on iOS (needs manual instructions)
    isDismissed,        // User dismissed this session
    isInstalling,       // Currently showing install prompt
    shouldShowInstallUI,// Combined check for showing UI
    
    // Actions
    installApp,         // Trigger install prompt
    dismissPrompt,      // Dismiss for session
    resetDismissal,     // Reset dismissal state
  };
};

export default usePWAInstall;
